import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { router } from "@inertiajs/react"
import { IconTrash } from "@tabler/icons-react"
import { useState } from "react"
import type { BoardDetail } from "../types"

interface BoardShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  board: BoardDetail
  shareableUsers: { id: number; name: string; email: string }[]
}

export function BoardShareDialog({ open, onOpenChange, board, shareableUsers }: BoardShareDialogProps) {
  const [userId, setUserId] = useState("")
  const [access, setAccess] = useState("viewer")

  const reload = { preserveScroll: true, only: ["board", "shareable_users"] }

  const saveSharing = (visibility: string, defaultAccess: string) =>
    router.patch(`/boards/${board.key}/sharing`, { board: { visibility, default_access: defaultAccess } }, reload)

  const addShare = () => {
    if (!userId) return

    router.post(
      `/boards/${board.key}/shares`,
      { board_share: { user_id: Number(userId), access } },
      {
        ...reload,
        onSuccess: () => {
          setUserId("")
          setAccess("viewer")
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share {board.name}</DialogTitle>
          <DialogDescription>Everyone you add can see this board and every ticket on it.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="board_visibility">Who can find it</Label>
          <Select value={board.visibility} onValueChange={(value) => saveSharing(value, board.default_access)}>
            <SelectTrigger id="board_visibility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="private">Private — only people you add</SelectItem>
              <SelectItem value="company">Company — everyone at the company</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {board.visibility === "company" && (
          <div className="space-y-2">
            <Label htmlFor="board_default_access">What the company can do</Label>
            <Select value={board.default_access} onValueChange={(value) => saveSharing(board.visibility, value)}>
              <SelectTrigger id="board_default_access">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="view">Read the board</SelectItem>
                <SelectItem value="edit">Move and file tickets</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <Separator />

        <div className="space-y-3">
          {board.shares.length === 0 && <p className="text-muted-foreground text-sm">Not shared with anyone yet.</p>}

          {board.shares.map((share) => (
            <div key={share.id} className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{share.name}</p>
                <p className="text-muted-foreground truncate text-xs">{share.email}</p>
              </div>

              <Select
                value={share.access}
                onValueChange={(value) =>
                  router.patch(`/boards/${board.key}/shares/${share.id}`, { board_share: { access: value } }, reload)
                }
              >
                <SelectTrigger className="w-32" aria-label={`Access for ${share.name}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Can view</SelectItem>
                  <SelectItem value="editor">Can edit</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove ${share.name}`}
                onClick={() => router.delete(`/boards/${board.key}/shares/${share.id}`, reload)}
              >
                <IconTrash className="size-4" />
              </Button>
            </div>
          ))}
        </div>

        <Separator />

        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1 space-y-2">
            <Label htmlFor="board_share_user">Add someone</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger id="board_share_user">
                <SelectValue placeholder="Pick a person" />
              </SelectTrigger>
              <SelectContent>
                {shareableUsers.map((user) => (
                  <SelectItem key={user.id} value={String(user.id)}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Select value={access} onValueChange={setAccess}>
            <SelectTrigger className="w-32" aria-label="Access for the new person">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="viewer">Can view</SelectItem>
              <SelectItem value="editor">Can edit</SelectItem>
            </SelectContent>
          </Select>

          <Button onClick={addShare} disabled={!userId}>
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
